import { useUser } from '@application/authentication/use_user';
import { useFeatureFlag } from '@application/feature_flags/use_feature_flag';
import { useHeader } from '@application/header_context';
import { withTranslation } from '@application/i18n/with_translation';
import { UnitService } from '@application/unit.service';
import { BreakPoints, Button, DialogAlert, Flex, HeadlessButton, Icon, SafeArea, Text, TextLink, useBreakpoint } from '@cellula/react';
import { primitiveColorTokens, tokens } from '@cellula/react-theme-patient';
import { RouterService } from '@cortex-shim/router.service';
import { Auth } from '@feature/auth/auth.component';
import { usePatientRepresentation } from '@feature/representation/use-patient-representation';
import { useRepresentablePatients } from '@feature/representation/use-representable-patients';
import { useResolve } from '@visiba/instantiation';
import { MouseEventHandler } from 'react';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import { Logo } from './logo.component';
import { Nav } from './nav.components';
import { useNHSApp } from './nhs_context/nhs_context';

export const HEADER_HEIGHT = {
	small: '56px',
	medium: '72px',
} as const;
export const REPRESENTATION_BANNER_HEIGHT = '40px';

const headerElements = {
	container: styled.div<{ $hasRepresentationBanner: boolean }>`
		position: fixed;
		z-index: 1;
		top: 0;
		left: 0;
		width: 100vw;
		background: ${tokens.color.bg.surface};
		box-shadow: ${({ $hasRepresentationBanner }) => ($hasRepresentationBanner ? 'none' : tokens.elevation.skim)};
	`,
	bar: styled.div`
		display: flex;
		padding: ${tokens.spacing.x0_5} ${tokens.spacing.x1_5};
		gap: ${tokens.spacing.x2};
		align-items: center;
		justify-content: space-between;
		box-sizing: border-box;
		min-height: ${HEADER_HEIGHT.small};

		${tokens.breakpointQuery.medium} {
			padding: ${tokens.spacing.x1} ${tokens.spacing.x2};
			gap: ${tokens.spacing.x4};
			min-height: ${HEADER_HEIGHT.medium};
		}
	`,
	representationBanner: styled.div`
		display: flex;
		align-items: center;
		background-color: ${primitiveColorTokens.primary['100']};
		box-sizing: border-box;
		min-height: ${REPRESENTATION_BANNER_HEIGHT};

		${tokens.breakpointQuery.medium} {
			border-top: 1px solid ${tokens.color.border.brand};
			border-bottom: 1px solid ${tokens.color.border.brand};
		}
	`,
	main: styled.div`
		display: flex;
		gap: ${tokens.spacing.x3};
		overflow: hidden;
	`,
	user: styled.div`
		display: flex;
		gap: ${tokens.spacing.x2};
		width: auto;
		white-space: nowrap;
		justify-content: flex-end;
		align-items: center;
	`,
};

export function Header() {
	const breakpoint = useBreakpoint();
	const nhsContext = useNHSApp();
	const hasNhsNavigationFeature = useFeatureFlag('PatientWeb.NHS.Navigation');
	const { visibility } = useHeader();
	const { currentlyRepresentedPatient } = useRepresentablePatients();
	const { stopRepresentation, isLoading } = usePatientRepresentation();
	const unitService = useResolve(UnitService);
	const location = useLocation();
	const showLicenceHolderName = location.pathname.startsWith('/sign-in');

	const nhsBackAction: MouseEventHandler<HTMLAnchorElement> = (e) => {
		e.preventDefault();
		try {
			if (nhsContext.isInNHSApp && nhsContext.backAction) {
				nhsContext.backAction();
			}
		} catch (err) {
			console.error('NHS App call failed:', err);
		}
	};

	return (
		<headerElements.container $hasRepresentationBanner={currentlyRepresentedPatient != null}>
			<headerElements.bar>
				<headerElements.main>
					<Flex align='center' gap={tokens.spacing.x1} direction='column'>
						<Logo />
						{/* Required by Google Play: a reference to the licence holder entity must appear under the logo on the sign-in page. */}
						{showLicenceHolderName && (
							<Text.p variant='labelSmallRegular' color={tokens.color.content.muted}>
								{unitService.rootUnit.name}
							</Text.p>
						)}
						{hasNhsNavigationFeature && nhsContext?.isInNHSApp && (
							<TextLink href='#' onClick={nhsBackAction}>
								<Flex gap={tokens.spacing.x0_5} direction='row' align='center'>
									<Icon type='chevronLeft' size='small' />
									{withTranslation('Component.TopBar.BackText')}
								</Flex>
							</TextLink>
						)}
					</Flex>
					{breakpoint < BreakPoints.Medium ? null : (
						<Nav.Content>
							<Nav.ItemReception />
							{visibility.showSearchReception && <Nav.ItemSearchReception />}
							{visibility.showMyVisits && <Nav.ItemVisits />}
						</Nav.Content>
					)}
				</headerElements.main>
				<headerElements.user>
					{breakpoint < BreakPoints.Medium ? (
						<UserSectionForSmallDevices />
					) : (
						<UserSectionForMediumAndLargeDevices visibility={visibility} />
					)}
				</headerElements.user>
			</headerElements.bar>
			{currentlyRepresentedPatient && (
				<headerElements.representationBanner>
					<SafeArea maxWidth='632px' horizontalPadding={tokens.spacing.x2}>
						<Flex direction='row' gap={tokens.spacing.x1} align='center' justify='center' style={{ width: '100%' }}>
							<Text variant='labelSemiBold'>
								{withTranslation('PW.Component.TopBar.ActingForText', { name: currentlyRepresentedPatient.CommonName })}
							</Text>
							{isLoading ? (
								<Text variant='labelSemiBold'>{withTranslation('PW.Component.TopBar.StopRepresentingText')}</Text>
							) : (
								<DialogAlert
									header={withTranslation('PW.Component.TopBar.StopRepresentingDialog.Header')}
									description=''
									abortText={withTranslation('PW.Component.TopBar.StopRepresentingDialog.AbortText')}
									successText={withTranslation('PW.Component.TopBar.StopRepresentingDialog.SuccessText')}
									onSuccess={stopRepresentation}
									trigger={
										<Button variant='ghost' size='xSmall'>
											{withTranslation('PW.Component.TopBar.StopRepresentingText')}
										</Button>
									}
								/>
							)}
						</Flex>
					</SafeArea>
				</headerElements.representationBanner>
			)}
		</headerElements.container>
	);
}

function UserSectionForSmallDevices() {
	const routerService = useResolve(RouterService);
	const user = useUser();

	function handleSettingsClick() {
		routerService.navigateByUrl('/menu');
	}

	return (
		<Flex gap={tokens.spacing.x2} direction='row' align='center' justify='center'>
			{user ? (
				<Text variant='labelSmallRegular' color={tokens.color.content.muted} truncate>
					{user.firstName} B
				</Text>
			) : null}
			<elements.mobileUserButton onClick={handleSettingsClick}>
				<Text variant='labelSmallSemiBold'>Meny</Text>
				<Icon type='menu' size='small' />
			</elements.mobileUserButton>
		</Flex>
	);
}
interface UserSectionProps {
	visibility: { showUserProfile: boolean; showSignOut: boolean };
}

function UserSectionForMediumAndLargeDevices({ visibility }: UserSectionProps) {
	const user = useUser();

	return (
		<>
			{user ? (
				<>
					{visibility.showUserProfile && <Nav.ItemUser />}
					{visibility.showSignOut && <Auth.SignOutButton />}
				</>
			) : (
				<Auth.SignInButton />
			)}
		</>
	);
}

const elements = {
	container: styled.div`
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		z-index: 1;
		background: ${tokens.color.bg.surface};
		padding: ${tokens.spacing.x0_5} ${tokens.spacing.x1_5};
		box-shadow: ${tokens.elevation.skim};
		display: flex;
		gap: ${tokens.spacing.x2};
		align-items: center;

		${tokens.breakpointQuery.medium} {
			padding: ${tokens.spacing.x1_5} ${tokens.spacing.x2};
			gap: ${tokens.spacing.x4};
		}
	`,
	userSection: styled.div`
		margin-left: auto;
		width: 100%;
		justify-content: end;
		display: flex;

		${tokens.breakpointQuery.medium} {
			width: unset;
			flex-shrink: 1;
			margin-right: 0;
			white-space: nowrap;
		}
	`,
	mobileUserButton: styled(HeadlessButton)`
		all: unset;
		position: relative;
		color: ${tokens.color.content.default};
		cursor: default;
		padding: ${tokens.spacing.x1};
		display: flex;
		gap: ${tokens.spacing.x1};

		align-items: center;

		@media (hover: hover) {
			transition: background 150ms ease-in;
			border-radius: ${tokens.borderRadius.small};

			&:hover {
				background: ${tokens.color.bg.interactiveMutedHover};
			}
		}

		&:active {
			background: ${tokens.color.bg.interactiveMutedActive};
		}

		&:focus-visible {
			outline: 2px solid ${tokens.color.focus};
		}
	`,
};
